import { ToastQueue } from "@heroui/react";
import type { ButtonProps } from "@heroui/react";
import type { ReactNode } from "react";

const DEFAULT_TOAST_TIMEOUT_MS = 4000;

export type AppToastOptions = {
  description?: ReactNode;
  indicator?: ReactNode;
  variant?: "default" | "accent" | "success" | "warning" | "danger";
  actionProps?: ButtonProps;
  isLoading?: boolean;
  timeout?: number;
  onClose?: () => void;
};

/** Toast queue without View Transitions — avoids InvalidStateError with router.refresh(). */
export const appToastQueue = new ToastQueue({
  wrapUpdate: (update) => {
    update();
  },
});

/** 与 appToastQueue 同一实例（根 Provider 默认 top；保留别名兼容既有 import）。 */
export const copyToastQueue = appToastQueue;

const createAppToast = (queue: ToastQueue) => {
  const toastFn = (message: ReactNode, options?: AppToastOptions) => {
    const timeout =
      options?.timeout !== undefined ? options.timeout : DEFAULT_TOAST_TIMEOUT_MS;

    return queue.add(
      {
        title: message,
        description: options?.description,
        indicator: options?.indicator,
        variant: options?.variant ?? "default",
        actionProps: options?.actionProps,
        isLoading: options?.isLoading,
      },
      {
        timeout,
        onClose: () => {
          requestAnimationFrame(() => {
            options?.onClose?.();
          });
        },
      },
    );
  };

  toastFn.success = (message: ReactNode, options?: Omit<AppToastOptions, "variant">) =>
    toastFn(message, { ...options, variant: "success" });
  toastFn.danger = (message: ReactNode, options?: Omit<AppToastOptions, "variant">) =>
    toastFn(message, { ...options, variant: "danger" });
  toastFn.info = (message: ReactNode, options?: Omit<AppToastOptions, "variant">) =>
    toastFn(message, { ...options, variant: "accent" });
  toastFn.warning = (message: ReactNode, options?: Omit<AppToastOptions, "variant">) =>
    toastFn(message, { ...options, variant: "warning" });
  toastFn.close = (key: string) => queue.close(key);
  toastFn.pauseAll = () => queue.pauseAll();
  toastFn.resumeAll = () => queue.resumeAll();
  toastFn.clear = () => queue.clear();
  toastFn.getQueue = () => queue.getQueue();

  return toastFn;
};

export const toast = createAppToast(appToastQueue);

/** 一键复制成功/失败提示（顶部 placement，与 toast 同队列）。 */
export const copyToast = createAppToast(appToastQueue);

/** 顶部业务提示（与 toast / copyToast 同队列）。 */
export const topToast = createAppToast(appToastQueue);
